<template>
	<v-card>
		<v-card-title class="dialog-header sgnl-subtitle-medium">
			Contact Sales
			<v-spacer />
			<v-btn
				icon
				variant="text"
				size="small"
				@click="$emit('close-dialog')"
			>
				<Close16 />
			</v-btn>
		</v-card-title>
		<v-divider />
		<v-card-text class="dialog-body">
			<v-form
				v-model="formIsValid"
				@submit.prevent="onSubmit"
				ref="contactSalesForm"
				action="/"
				class="stack"
			>
				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.plan }}</div>
					<v-select
						v-model="formValues.plan"
						required
						disabled
						:rules="formRules.plan"
						:items="planSelectItems"
						hide-details="auto"
					/>
				</div>

				<v-divider />

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.fullname }}</div>
					<v-text-field
						v-model="formValues.fullname"
						name="name"
						autocomplete="name"
						:rules="formRules.fullname"
						validate-on-blur
						required
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.email }}</div>
					<v-text-field
						v-model="formValues.email"
						type="email"
						name="email"
						autocomplete="email"
						:rules="formRules.email"
						required
						validate-on-blur
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.whyPreferThisTier }}</div>
					<v-textarea
						v-model="formValues.whyPreferThisTier"
						rows="4"
						auto-grow
						:rules="formRules.whyPreferThisTier"
						hint="Let us know what features or capabilities are important to you."
						persistent-hint
						hide-details="auto"
					/>
				</div>

				<div class="input-group">
					<div class="field-label sgnl-text-color-default">{{ labelValues.additionalRequests }}</div>
					<v-textarea
						v-model="formValues.additionalRequests"
						rows="4"
						auto-grow
						:rules="formRules.additionalRequests"
						hint="Any specific requirements or questions you'd like to discuss with our sales team."
						persistent-hint
						hide-details="auto"
					/>
				</div>
			</v-form>
		</v-card-text>
		<v-divider />
		<v-card-actions class="dialog-footer">
			<v-btn
				variant="outlined"
				@click="$emit('close-dialog')"
				>Cancel</v-btn
			>
			<v-btn
				variant="flat"
				color="primary"
				:loading="submitButtonLoading"
				:disabled="!formIsValid"
				@click="onSubmit"
				>Submit</v-btn
			>
		</v-card-actions>
	</v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import { fetchAuthSession } from "aws-amplify/auth";
import { joinWaitlist } from "@/js/signaloidClient";
import { monitoringCaptureError } from "@/plugins/monitoring";

export default defineComponent({
	name: "ContactSalesForm",
	components: { Close16 },
	emits: ["form-submitted", "form-submission-failed", "close-dialog"],
	data: () => ({
		formIsValid: false,
		submitButtonLoading: false,
		formPostSuccess: false,
		formValues: {
			plan: "",
			fullname: "",
			email: "",
			whyPreferThisTier: "",
			additionalRequests: "",
		},
		formRules: {
			plan: [(v: string) => (v && v.length > 0) || "This field is required."],
			fullname: [(v: string) => (v && v.length > 0) || "This field is required."],
			email: [
				(v: string) => (v && v.length > 0) || "This field is required.",
				(v: string) => /.+@.+\..+/.test(v) || "Please enter a valid email.",
			],
			whyPreferThisTier: [(v: string) => (v && v.length > 0) || "This field is required."],
			additionalRequests: [],
		},
		labelValues: {
			plan: "Plan you are interested in",
			fullname: "Full Name",
			email: "Email",
			whyPreferThisTier: "Why do you prefer this Tier?",
			additionalRequests: "Additional requests",
		},
		planSelectItems: ["Enterprise S", "Enterprise M", "Enterprise L"],
	}),
	props: {
		passedFullname: { type: String, default: null },
		passedEmail: { type: String, default: null },
		passedPlan: { type: String, default: null },
	},
	watch: {
		passedPlan: function () {
			this.registerPassedProps();
		},
	},
	mounted() {
		this.registerPassedProps();
	},
	methods: {
		registerPassedProps() {
			this.formValues.fullname = this.passedFullname || "";
			this.formValues.email = this.passedEmail || "";
			if (this.planSelectItems.includes(this.passedPlan)) {
				this.formValues.plan = this.passedPlan || "";
			}
		},
		async onSubmit() {
			this.formPostSuccess = false;
			// @ts-ignore
			if (!this.$refs.contactSalesForm.validate()) {
				return;
			}

			const message: Record<string, any> = {};
			message["contact-sales-form"] = this.formValues;
			message["form-labels"] = this.labelValues;

			const planSelectItems = this.planSelectItems;
			message["form-choices"] = {
				planSelectItems,
			};

			/*
			 *  Build a pretty version of the message.
			 */
			let messagePretty = "";
			messagePretty += "A user has completed the Contact Sales form.\n\n";

			for (const key in this.labelValues) {
				if (Object.hasOwnProperty.call(this.labelValues, key)) {
					messagePretty += `${this.labelValues[key]} : ${this.formValues[key]}\n`;
				}
			}

			try {
				const session = await fetchAuthSession();
				const idToken = session.tokens?.idToken;
				messagePretty += "\n# User Metadata\n";
				messagePretty += `CognitoUser email: ${idToken?.payload?.["cognito:username"] ?? ""}\n`;
				messagePretty += `CognitoUser sub: ${idToken?.payload?.["sub"] ?? ""}\n`;
				messagePretty += `CognitoUser name: ${idToken?.payload?.["name"] ?? ""}\n`;
				messagePretty += `CognitoUser groups: ${idToken?.payload?.["cognito:groups"] ?? ""}\n`;
			} catch (error) {
				monitoringCaptureError(error, "Submit contact sales form");
			}

			try {
				this.submitButtonLoading = true;
				await joinWaitlist(messagePretty);
				// @ts-ignore
				this.$refs.contactSalesForm.reset();
				// @ts-ignore
				this.$posthog?.capture("submitted_contact_sales_form", {
					plan: this.formValues.plan,
				});

				// Emit event to close dialog and show snackbar
				this.$emit("form-submitted");
			} catch (error) {
				monitoringCaptureError(error, "Submit contact sales form");
				// Emit failure event
				this.$emit("form-submission-failed");
			} finally {
				this.submitButtonLoading = false;
			}
		},
	},
});
</script>
